import React from 'react';
import styled from 'styled-components';
import { Clock, User, Package, DollarSign, CheckCircle, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { useBraipWebhookHistory, useClearBraipWebhookHistory } from '../../hooks/useBraip';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const HistoryCard = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
`;

const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[5]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HistoryTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  background: #ef4444;
  color: white;
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast};

  &:hover {
    background: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HistoryList = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const WebhookItem = styled.div`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: background ${props => props.theme.animations.duration.fast};

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const WebhookRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[4]};
`;

const WebhookInfo = styled.div`
  flex: 1;
`;

const WebhookMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const WebhookClient = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
`;

const WebhookProduct = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const WebhookValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const WebhookMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textTertiary};
  margin-top: ${props => props.theme.spacing[2]};
`;

const WebhookStatus = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};

  ${props => {
    if (props.$status === 'aprovado' || props.$status === 'pago') {
      return `
        background: rgba(52, 211, 153, 0.1);
        color: ${props.theme.colors.success};
      `;
    } else if (props.$status === 'cancelada' || props.$status === 'chargeback') {
      return `
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      `;
    } else {
      return `
        background: rgba(251, 191, 36, 0.1);
        color: #f59e0b;
      `;
    }
  }}
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing[12]} ${props => props.theme.spacing[6]};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

interface BraipWebhookHistoryProps {
  userId?: string;
  limit?: number;
}

const BraipWebhookHistory: React.FC<BraipWebhookHistoryProps> = ({
  userId: propUserId,
  limit = 50
}) => {
  const { user } = useCurrentUser();
  const userId = propUserId || user?.uid;
  const { data: history, isLoading } = useBraipWebhookHistory(userId, limit);
  const clearMutation = useClearBraipWebhookHistory(userId);

  const handleClear = async () => {
    if (confirm('Tem certeza que deseja limpar todo o histórico de webhooks?')) {
      try {
        await clearMutation.mutateAsync();
      } catch (error) {
        console.error('Erro ao limpar histórico:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const getStatusIcon = (status: string) => {
    if (status === 'aprovado' || status === 'pago') {
      return <CheckCircle size={16} />;
    } else if (status === 'cancelada' || status === 'chargeback') {
      return <XCircle size={16} />;
    } else {
      return <AlertCircle size={16} />;
    }
  };

  return (
    <HistoryCard>
      <HistoryHeader>
        <HistoryTitle>
          <Clock size={24} />
          Histórico de Webhooks
        </HistoryTitle>
        {history && history.webhooks.length > 0 && (
          <ClearButton
            onClick={handleClear}
            disabled={clearMutation.isPending}
          >
            <Trash2 size={16} />
            Limpar Histórico
          </ClearButton>
        )}
      </HistoryHeader>

      <HistoryList>
        {isLoading && (
          <EmptyState>
            Carregando histórico...
          </EmptyState>
        )}

        {!isLoading && (!history || history.webhooks.length === 0) && (
          <EmptyState>
            Nenhum webhook recebido ainda
          </EmptyState>
        )}

        {!isLoading && history && history.webhooks.map((webhook) => (
          <WebhookItem key={webhook.transactionId}>
            <WebhookRow>
              <WebhookInfo>
                <WebhookMainInfo>
                  <User size={16} />
                  <WebhookClient>{webhook.clientName}</WebhookClient>
                </WebhookMainInfo>
                <WebhookProduct>
                  <Package size={14} />
                  {webhook.productName}
                </WebhookProduct>
                <WebhookMeta>
                  <span>ID: {webhook.transactionId}</span>
                  <span>•</span>
                  <span>{formatDate(webhook.receivedAt)}</span>
                  {!webhook.processed && webhook.errorMessage && (
                    <>
                      <span>•</span>
                      <span style={{ color: '#ef4444' }}>
                        Erro: {webhook.errorMessage}
                      </span>
                    </>
                  )}
                </WebhookMeta>
              </WebhookInfo>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                <WebhookValue>
                  {formatCurrency(webhook.transValue)}
                </WebhookValue>
                <WebhookStatus $status={webhook.transStatus}>
                  {getStatusIcon(webhook.transStatus)}
                  {webhook.transStatus}
                </WebhookStatus>
              </div>
            </WebhookRow>
          </WebhookItem>
        ))}
      </HistoryList>
    </HistoryCard>
  );
};

export default BraipWebhookHistory;
